import { Button } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { createTemporaryLink, createPermanentLink, getMaterials, updateStatisticsFunc } from '../../Common/Database';
import Loader from '../../Components/Loader';
import { getUserType } from '../../redux/slices/userSlice';

import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import './style.scss';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const MaterialView = () => {
  const query = useQuery();

  const [pageNumber, setPageNumber] = useState(1);
  const [inputPageNumber, setInputPageNumber] = useState(pageNumber.toString());
  const [prevPageNumber, setPrevPageNumber] = useState(pageNumber);
  const [numPages, setNumPages] = useState();
  const [link, setLink] = useState();
  const { t } = useTranslation();
  const location = useLocation();
  const params = useParams();
  const userTypeSelector = useSelector(getUserType);
  const [materialItem, setMaterialItem] = useState();
  const [materialView, setMaterialView] = useState();
  const [fileSize, setFileSize] = useState(0);
  const [loadedSize, setLoadedSize] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);

  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

  const getFileSize = async url => {
    const response = await fetch(url);
    const size = response.headers.get('content-length');
    return size ? parseInt(size, 10) : 0;
  };

  useEffect(() => {
    const fetchFileSize = async () => {
      const size = await getFileSize(materialView.state.url);
      setFileSize(size);
    };

    if (materialView && materialView.state) {
      fetchFileSize();
    }
  }, [materialView]);

  useEffect(() => {
    setLoadingProgress(fileSize > 0 ? Math.round((loadedSize / fileSize) * 100) : 0);
  }, [loadedSize, fileSize]);

  const onDocumentLoadProgress = progressData => {
    setLoadedSize(progressData.loaded);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const temporaryLink = () => {
    const id = uuidv4();
    const state = materialView.state;
    const linkUrl = state.url;
    const linkType = state.type;

    createTemporaryLink(id, linkUrl, pageNumber, linkType);
    updateStatisticsFunc('AllCreateLinksMaterials', 'increment');
    setLink(`${window.location.protocol}//${window.location.host}/publicMaterial/${id}`);
  };

  const permanentLink = () => {
    const state = materialView.state;
    const id = state.id;
    const linkUrl = state.url;
    const linkType = state.type;

    createPermanentLink(id + pageNumber, linkUrl, pageNumber, linkType).then(() => {
      setLink(`${window.location.protocol}//${window.location.host}/publicMaterial/${id + pageNumber}`);
    });
  };

  const onPageNumInputChange = e => {
    const inputVal = e.target.value;
    const pattern = /^\d*$/;
    if (pattern.test(inputVal)) {
      setInputPageNumber(inputVal);
      const pageNum = parseInt(inputVal);
      if (!isNaN(pageNum)) {
        setPageNumber(Math.min(Math.max(1, pageNum), numPages));
        setPrevPageNumber(pageNum);
      }
    } else {
      setInputPageNumber(prevPageNumber.toString());
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link);
    updateStatisticsFunc('AllCreateLinksMaterials', 'increment');
  };

  useEffect(() => {
    const showLocation = async () => {
      if (location && location.state) {
        setMaterialView(location);
      } else if (materialItem && materialItem.state) {
        setMaterialView(materialItem);
      } else {
        const materials = await getMaterials();
        const exactMaterial = materials.filter(material => material.id === query.get('id'));
        if (exactMaterial[0]) {
          setMaterialView({ state: exactMaterial[0] });
        }
      }
    };

    showLocation();
  }, [location, materialItem]);

  useEffect(() => {
    console.log('params', userTypeSelector);
  }, [userTypeSelector]);

  useEffect(() => {
    setInputPageNumber(pageNumber.toString());
  }, [pageNumber]);

  useEffect(() => {
    if (location.state) {
      setMaterialItem(location);
    }
  }, [location]);

  return (
    <div className="material-view-page container">
      <div className="material-view_content">
        {materialView && (
          <>
            <div className="action-panel">
              {params && !params.user && userTypeSelector !== 'Student' && (
                <>
                  <button onClick={temporaryLink} className="btn btn-primary more btn-active">
                    {t('temporary link')}
                  </button>

                  <button onClick={permanentLink} className="btn btn-primary more btn-active">
                    {t('permanent link')}
                  </button>
                </>
              )}

              {link && (
                <div className="link-pannel">
                  <div className="link-wrap">
                    <p>{link}</p>
                  </div>
                  <button onClick={handleCopyLink} className="btn more btn-view">
                    {t('copyLink')}
                  </button>
                </div>
              )}
            </div>

            <div className="lesson-panel-wrap">
              <Button
                disabled={pageNumber <= 1}
                onClick={() => {
                  setPageNumber(pageNumber - 1);
                }}
                variant="contained"
                className="btn btn-icon btn-left"
              >
                <span className="icon icon-chevron-left"></span>
              </Button>

              <div className="lesson-panel">
                {materialView && materialView.state.type !== 'Picture' ? (
                  <Document
                    className="doc"
                    loading={<Loader progress={loadingProgress} />}
                    file={materialView.state.url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadProgress={onDocumentLoadProgress}
                  >
                    <Page className="doc-page" pageNumber={pageNumber} />
                  </Document>
                ) : (
                  <>
                    <img src={materialView.state.url}></img>
                  </>
                )}
              </div>

              <Button
                disabled={pageNumber >= numPages}
                onClick={() => {
                  setPageNumber(pageNumber + 1);
                }}
                variant="contained"
                className="btn btn-icon btn-right"
              >
                <span className="icon icon-chevron-right"></span>
              </Button>
            </div>

            {materialView && materialView.state.type !== 'Picture' && (
              <div className="pagination">
                <div className="page-number">
                  {t('page')}{' '}
                  <input
                    type="text"
                    className="page-input"
                    value={inputPageNumber}
                    onChange={onPageNumInputChange}
                    pattern="\d*"
                  />{' '}
                  {t('from')} {numPages || '--'}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MaterialView;
